// A circle's places (geofences): list, create (anywhere or at a member's location),
// move/resize on the map, rename and delete. Names and the address can come from the geocoder.
import type { Schemas } from "@10twenty/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { api, ApiError, errorMessage, unwrap } from "../api";
import { useMe } from "../auth/AuthContext";
import { agoText } from "../live/liveCircle";
import {
  ADDRESS_MAX,
  createBody,
  draftErrors,
  draftOf,
  fillDraft,
  type Filled,
  formatRadius,
  isAt,
  lookupPosition,
  memberStarts,
  moveDraft,
  NAME_MAX,
  nameSuggestions,
  pickedCenter,
  PLACE_LIMIT,
  radiusToSlider,
  SLIDER_STEPS,
  sliderToRadius,
  memberFrame,
  sortPlaces,
  startAt,
  updateBody,
  NOTHING_FILLED,
  type MemberStart,
  type PlaceDraft,
} from "../places/places";
import { useNow } from "../useNow";

const PlacesMap = lazy(() => import("../map/PlacesMap"));

type Place = Schemas["Place"];

/**
 * An open editor: `place` is null for a new place; `session` tells editing
 * sessions apart, and `focus` counts jumps (to a member) the map should frame.
 */
interface Editing {
  place: Place | null;
  draft: PlaceDraft;
  session: number;
  focus: number;
  filled: Filled;
}

/** Waits this long after the center stops moving before looking up names. */
const LOOKUP_DELAY_MS = 500;
/** A member's position older than this shows its age on the chip. */
const OLD_START_MS = 60 * 60_000;

function mutationError(error: unknown): string {
  if (error instanceof ApiError && error.code === "place_limit")
    return `This circle already has the maximum of ${PLACE_LIMIT} places.`;
  return errorMessage(error, {
    400: "Check the name, address and radius, then try again.",
    404: "This place no longer exists, or you're no longer in this circle.",
  });
}

export function PlacesPage() {
  const circleId = useParams().circleId ?? "";
  // A fresh subtree per circle: no editor or camera state carries over.
  return <PlacesView key={circleId} circleId={circleId} />;
}

function PlacesView({ circleId }: { circleId: string }) {
  const queryClient = useQueryClient();
  const me = useMe();
  const nowMs = useNow(60_000);
  const [searchParams, setSearchParams] = useSearchParams();
  const panelRef = useRef<HTMLElement>(null);
  const sessions = useRef(0);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [touched, setTouched] = useState(false);

  const circle = useQuery({
    queryKey: ["circle", circleId],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}", { params: { path: { circleId } } })),
  });
  const places = useQuery({
    queryKey: ["circle", circleId, "places"],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}/places", { params: { path: { circleId } } })),
  });
  const sorted = useMemo(() => sortPlaces(places.data ?? []), [places.data]);
  // With no places yet, the map opens on the members instead of the whole world.
  const noPlaces = places.data?.length === 0;
  const state = useQuery({
    queryKey: ["circle", circleId, "members", "state"],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}/members/state", { params: { path: { circleId } } })),
    // Keeps the "Start at" chips in step with pauses and sharing bubbles while creating.
    refetchInterval: editing !== null && editing.place === null ? 30_000 : false,
  });
  const members = useQuery({
    queryKey: ["circle", circleId, "members"],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}/members", { params: { path: { circleId } } })),
  });
  // Positions only from a snapshot fetched here and not failed since: a cached one may
  // predate a member pausing or sharing only an area.
  const freshState = state.isFetchedAfterMount && !state.isError ? state.data : undefined;
  const starts = useMemo(
    () => memberStarts(members.data ?? [], freshState?.members ?? [], me.data?.id ?? null),
    [members.data, freshState, me.data],
  );
  const fallback = useMemo(
    () =>
      !noPlaces
        ? null
        : state.data
          ? memberFrame(state.data.members.flatMap((m) => (m.location ? [m.location] : [])))
          : state.error
            ? []
            : null,
    [noPlaces, state.data, state.error],
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["circle", circleId, "places"] });
  const save = useMutation({
    mutationFn: async ({ place, draft }: Editing) => {
      if (!place) {
        const body = createBody(draft);
        if (!body) return;
        unwrap(await api.POST("/circles/{circleId}/places", { params: { path: { circleId } }, body }));
        return;
      }
      const body = updateBody(place, draft);
      if (!body) return;
      unwrap(
        await api.PATCH("/circles/{circleId}/places/{placeId}", {
          params: { path: { circleId, placeId: place.id } },
          body,
        }),
      );
    },
    onSettled: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (placeId: string) => {
      const result = await api.DELETE("/circles/{circleId}/places/{placeId}", {
        params: { path: { circleId, placeId } },
      });
      // Already deleted (by someone else) is what we wanted.
      if (!result.response.ok && result.response.status !== 404) unwrap(result);
    },
    onSettled: invalidate,
  });

  function open(place: Place | null, start?: MemberStart) {
    // Never swap the editor while its save or delete is in flight.
    if (save.isPending || remove.isPending) return;
    save.reset();
    remove.reset();
    setConfirmDelete(false);
    setTouched(false);
    const draft = draftOf(place ?? undefined);
    setEditing({
      place,
      draft: start ? { ...draft, ...startAt(start) } : draft,
      session: ++sessions.current,
      focus: 0,
      filled: NOTHING_FILLED,
    });
    // Members' positions for the "Start at" chips.
    if (!place) void state.refetch();
  }
  const close = () => setEditing(null);
  const change = (patch: Partial<PlaceDraft>) =>
    setEditing((prev) => (prev ? { ...prev, draft: { ...prev.draft, ...patch } } : prev));
  /** Moves the center; `jump` has the map frame the new spot. */
  const move = (patch: Partial<PlaceDraft>, jump = false) =>
    setEditing((prev) =>
      prev ? { ...prev, ...moveDraft(prev.draft, prev.filled, patch), focus: prev.focus + (jump ? 1 : 0) } : prev,
    );
  const fill = (patch: { name: string } | { address: string }) =>
    setEditing((prev) => (prev ? { ...prev, ...fillDraft(prev.draft, prev.filled, patch) } : prev));

  // `?new=<userId>` (the map's "Save as place") opens a new place at that member, once.
  const newFor = searchParams.get("new");
  const handledNew = useRef(false);
  // Never from a cached snapshot: the member may have paused since.
  const startsReady = (members.isFetchedAfterMount || members.isError) && (state.isFetchedAfterMount || state.isError);
  useEffect(() => {
    if (newFor === null || handledNew.current || !places.data || !startsReady) return;
    handledNew.current = true;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("new");
        return next;
      },
      { replace: true },
    );
    if (places.data.length >= PLACE_LIMIT) return;
    const start = starts.find((candidate) => candidate.userId === newFor);
    const draft = draftOf();
    const session = ++sessions.current;
    setEditing(
      (prev) =>
        prev ?? {
          place: null,
          draft: start ? { ...draft, ...startAt(start) } : draft,
          session,
          focus: 0,
          filled: NOTHING_FILLED,
        },
    );
  }, [newFor, places.data, startsReady, starts, setSearchParams]);

  // Names and an address for the draft's center, looked up once it has settled. Only for
  // new places and places without an address: the lookup sends the position to the geocoder.
  const [geocoderOff, setGeocoderOff] = useState(false);
  const center =
    editing && editing.draft.lat !== null && editing.draft.lon !== null
      ? lookupPosition(editing.draft.lat, editing.draft.lon)
      : null;
  const wantLookup = !geocoderOff && center !== null && (!editing?.place || !editing.place.address);
  const [settled, setSettled] = useState<{ lat: number; lon: number } | null>(null);
  const centerLat = center?.lat ?? null;
  const centerLon = center?.lon ?? null;
  useEffect(() => {
    if (!wantLookup || centerLat === null || centerLon === null) return;
    const timer = setTimeout(() => setSettled({ lat: centerLat, lon: centerLon }), LOOKUP_DELAY_MS);
    return () => clearTimeout(timer);
  }, [wantLookup, centerLat, centerLon]);
  const fresh = settled !== null && settled.lat === centerLat && settled.lon === centerLon;
  const suggestions = useQuery({
    queryKey: ["place-suggestions", circleId, settled?.lat, settled?.lon],
    queryFn: async () => {
      try {
        return unwrap(
          await api.GET("/circles/{circleId}/place-suggestions", {
            params: { path: { circleId }, query: { lat: settled!.lat, lon: settled!.lon } },
          }),
        );
      } catch (error) {
        if (error instanceof ApiError && error.code === "geocoding_disabled") setGeocoderOff(true);
        throw error;
      }
    },
    enabled: wantLookup && fresh,
    staleTime: Infinity,
    retry: false,
  });
  const suggested = wantLookup && fresh ? suggestions.data : undefined;

  function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (!editing || Object.keys(draftErrors(editing.draft)).length) return;
    save.mutate(editing, { onSuccess: close });
  }

  const draft = editing?.draft;
  const errors = draft ? draftErrors(draft) : {};
  const busy = save.isPending || remove.isPending;
  const full = (places.data?.length ?? 0) >= PLACE_LIMIT;
  const title = circle.data?.name ?? "Circle";

  return (
    <div className="places-page">
      <Suspense fallback={<div className="live-map" />}>
        <PlacesMap
          places={places.data ?? []}
          editingId={editing?.place?.id ?? null}
          draft={
            draft && draft.lat !== null && draft.lon !== null ? { ...draft, lat: draft.lat, lon: draft.lon } : null
          }
          fitKey={circleId}
          fallback={fallback}
          focusKey={editing ? `${editing.session}:${editing.focus}` : null}
          onPick={editing && !busy ? (lat, lon) => move(pickedCenter(lat, lon)) : null}
          onSelect={(placeId) => {
            const place = places.data?.find((p) => p.id === placeId);
            if (place) open(place);
          }}
          overlayRef={panelRef}
        />
      </Suspense>
      <aside ref={panelRef} className="places-panel" aria-label="Places">
        <div className="places-header">
          <Link to={`/circles/${circleId}`} className="muted">
            ← {title}
          </Link>
          <h1>{editing ? (editing.place ? "Edit place" : "New place") : "Places"}</h1>
        </div>
        {!editing ? (
          <div className="places-body stack">
            {(circle.error ?? places.error) && (
              <p className="error">
                {errorMessage(circle.error ?? places.error, {
                  404: "This circle doesn't exist, or you're no longer a member.",
                })}
              </p>
            )}
            {places.isPending && !places.error && <p className="muted">Loading places…</p>}
            {places.data && (
              <>
                <p className="muted">
                  Places like home or school. Arrivals and departures will be shown to everyone in the circle.
                </p>
                <button type="button" onClick={() => open(null)} disabled={full}>
                  Add place
                </button>
                {full && <p className="hint">This circle has the maximum of {PLACE_LIMIT} places.</p>}
                {sorted.length === 0 ? (
                  <p className="muted">No places yet.</p>
                ) : (
                  <ul className="places-list">
                    {sorted.map((place) => (
                      <li key={place.id}>
                        <button type="button" onClick={() => open(place)}>
                          <span className="places-name">{place.name}</span>
                          <span className="places-meta">
                            {[formatRadius(place.radius_m), place.address].filter(Boolean).join(" · ")}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        ) : (
          <form className="places-body stack" onSubmit={submit} noValidate>
            <p className={touched && errors.center ? "error" : "hint"}>
              {draft!.lat === null
                ? !editing.place && starts.length
                  ? "Click the map where the place is, or start at someone's location."
                  : "Click the map where the place is."
                : "Click the map to move the place."}
            </p>
            {!editing.place && starts.length > 0 && (
              <div className="place-chips" role="group" aria-label="Start at someone's location">
                {starts.map((start) => {
                  const ago = agoText(nowMs, start.at) ?? `on ${new Date(start.at).toLocaleDateString()}`;
                  return (
                    <button
                      key={start.userId}
                      type="button"
                      className="secondary"
                      aria-pressed={isAt(draft!, start)}
                      title={`${start.label === "You" ? "Your" : `${start.label}'s`} location, ${ago}`}
                      disabled={busy}
                      onClick={() => move(startAt(start), true)}
                    >
                      📍 {start.label}
                      {nowMs - start.at > OLD_START_MS && <span className="place-chip-age"> · {ago}</span>}
                    </button>
                  );
                })}
              </div>
            )}
            <label>
              Name
              <input
                value={draft!.name}
                maxLength={NAME_MAX}
                required
                autoFocus
                disabled={busy}
                onChange={(event) => change({ name: event.target.value })}
              />
              {touched && errors.name && <span className="error">{errors.name}</span>}
            </label>
            {!editing.place && (
              <div className="place-chips" role="group" aria-label="Suggested names">
                {nameSuggestions(suggested?.names ?? [], places.data ?? []).map((name) => (
                  <button
                    key={name}
                    type="button"
                    className="secondary"
                    aria-pressed={draft!.name.trim() === name}
                    disabled={busy}
                    onClick={() => fill({ name })}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
            <label>
              Address (optional)
              <input
                value={draft!.address}
                maxLength={ADDRESS_MAX}
                autoComplete="street-address"
                disabled={busy}
                onChange={(event) => change({ address: event.target.value })}
              />
              {errors.address && <span className="error">{errors.address}</span>}
            </label>
            {suggested?.address && !draft!.address.trim() && (
              <button
                type="button"
                className="link-button place-address-suggestion"
                disabled={busy}
                onClick={() => fill({ address: suggested.address ?? "" })}
              >
                Use “{suggested.address}”
              </button>
            )}
            <label>
              Radius: {formatRadius(draft!.radiusM)}
              <input
                type="range"
                min={0}
                max={SLIDER_STEPS}
                step={1}
                value={radiusToSlider(draft!.radiusM)}
                aria-valuetext={formatRadius(draft!.radiusM)}
                disabled={busy}
                onChange={(event) => move({ radiusM: sliderToRadius(Number(event.target.value)) })}
              />
            </label>
            {save.error && <p className="error">{mutationError(save.error)}</p>}
            {remove.error && <p className="error">{mutationError(remove.error)}</p>}
            <div className="button-row">
              <button type="submit" disabled={busy}>
                {save.isPending ? "Saving…" : "Save"}
              </button>
              <button type="button" className="secondary" onClick={close} disabled={busy}>
                Cancel
              </button>
            </div>
            {editing.place &&
              (confirmDelete ? (
                <div className="button-row">
                  <button
                    type="button"
                    className="danger"
                    disabled={busy}
                    onClick={() => remove.mutate(editing.place!.id, { onSuccess: close })}
                  >
                    {remove.isPending ? "Deleting…" : `Delete ${editing.place.name}`}
                  </button>
                  <button type="button" className="secondary" disabled={busy} onClick={() => setConfirmDelete(false)}>
                    Keep it
                  </button>
                </div>
              ) : (
                <button type="button" className="danger" disabled={busy} onClick={() => setConfirmDelete(true)}>
                  Delete place
                </button>
              ))}
          </form>
        )}
      </aside>
    </div>
  );
}
